/**
 * DriverPortalPage.js — Public-facing driver portal
 * No login required. Drivers scan VIN/ROWO/stock_po, see unit status,
 * and log state transitions (factory pickup → arrive SNA → deliver customer).
 */
import React, { useEffect, useRef, useState } from 'react';
import { compressImages } from '../scripts/imageCompress';

const API = '/api/portal';

async function apiFetch(path, opts = {}) {
  const res = await fetch(API + path, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data.error || `HTTP ${res.status}`);
    e.status = res.status;
    throw e;
  }
  return data;
}

const STATE_LABELS = {
  ready_for_pickup:   { label: 'Ready for Factory Pickup', color: '#d97706', bg: '#fef3c7' },
  in_transit_to_sna:  { label: 'In Transit to SNA',        color: '#2563eb', bg: '#dbeafe' },
  at_sna:             { label: 'Arrived at SNA',            color: '#7c3aed', bg: '#ede9fe' },
  pdi_in_progress:    { label: 'PDI In Progress',           color: '#f59e0b', bg: '#fef3c7' },
  pdi_complete:       { label: 'PDI Complete — Ready to Deliver', color: '#0891b2', bg: '#cffafe' },
  delivered:          { label: 'Delivered to Customer',     color: '#16a34a', bg: '#dcfce7' },
};

const ACTION_LABELS = {
  mark_pickup:           'Mark: Picked Up from Factory',
  mark_arrive_sna:       'Mark: Arrived at SNA',
  mark_pdi_started:      'Mark: PDI Started',
  mark_pdi_completed:    'Mark: PDI Complete',
  mark_deliver_customer: 'Mark: Delivered to Customer',
};

// Driver-portal users only do pickup / arrive-at-sna / deliver-to-customer.
// PDI start/complete is recorded by techs in the PDI app (which auto-fires
// the state-machine transition server-side). The portal still displays the
// pdi_in_progress / pdi_complete states for visibility, but shows no action
// button in those states.
function stateToAction(state) {
  if (state === 'ready_for_pickup')  return 'mark_pickup';
  if (state === 'in_transit_to_sna') return 'mark_arrive_sna';
  if (state === 'at_sna')            return null;  // PDI must start — done in PDI app, not portal
  if (state === 'pdi_in_progress')   return null;  // PDI must complete — done in PDI app
  if (state === 'pdi_complete')      return 'mark_deliver_customer';
  return null;
}

// ── DB selector screen ────────────────────────────────────────────────────────
function DbSelector({ onSelect }) {
  const [dbs,     setDbs]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/public/dbs')
      .then(r => r.json())
      .then(d => setDbs(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="container" style={{ maxWidth: 480, paddingTop: 48 }}>
      <div style={{ fontWeight: 900, fontSize: 28, marginBottom: 8 }}>Driver Portal</div>
      <div className="muted" style={{ marginBottom: 24 }}>Select your database to get started.</div>

      {loading && <div className="muted" style={{ marginBottom: 16 }}>Loading…</div>}

      {!loading && dbs.length === 0 && (
        <div style={{ color: '#dc2626', marginBottom: 16, fontWeight: 700 }}>No databases found. Contact your supervisor.</div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {dbs.map(d => (
          <button
            key={d.dbId}
            className="btn"
            onClick={() => onSelect(d.dbId)}
            style={{
              padding: '18px 20px',
              fontSize: 18,
              fontWeight: 800,
              textAlign: 'left',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <span>{d.name || d.dbId}</span>
            <span style={{ fontSize: 12, opacity: 0.6, fontFamily: 'monospace', fontWeight: 400 }}>{d.dbId}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Driver login (PIN only — each driver has a unique PIN) ─────────────────
function DriverLogin({ dbId, onLoggedIn, onChangeDb }) {
  const [pin, setPin]   = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const pushDigit = (d) => setPin(p => (p.length < 12 ? p + d : p));
  const backspace = () => setPin(p => p.slice(0, -1));
  const disabled  = pin.length < 4 || busy;

  const submit = async (e) => {
    e && e.preventDefault();
    if (disabled) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch('/api/portal/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ db: dbId, pin }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);

      // Dispatch issued this driver a temp PIN — force them to pick a new
      // one before they can use the rest of the portal. We pass the current
      // PIN along so the change-PIN modal can submit it as `current_pin`
      // without making them type it again on a touch keypad.
      if (data.must_change_pin || data.driver?.must_change_pin) {
        onLoggedIn(data.session_token, data.driver, { tempPin: pin });
      } else {
        onLoggedIn(data.session_token, data.driver);
      }
    } catch (ex) {
      setErr(ex.message || 'Login failed');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 420, paddingTop: 36 }}>
      <div style={{ fontWeight: 900, fontSize: 26, marginBottom: 4 }}>Driver Portal</div>
      <div className="muted" style={{ marginBottom: 18, fontSize: 14 }}>
        Enter your PIN. DB: <code>{dbId}</code>
        &nbsp;·&nbsp;<button onClick={onChangeDb} className="btn-ghost" style={{ padding: '2px 6px', fontSize: 12 }}>change</button>
      </div>

      <form onSubmit={submit}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>PIN</div>
        <div style={{
          padding: '14px 16px', fontSize: 28, textAlign: 'center', letterSpacing: 12,
          background: 'var(--card-bg)', border: '1px solid var(--card-border)',
          borderRadius: 10, fontFamily: 'monospace', marginBottom: 10, minHeight: 28,
        }}>
          {pin.replace(/./g, '•') || <span style={{ opacity: 0.3, letterSpacing: 0 }}>enter PIN</span>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
          {['1','2','3','4','5','6','7','8','9'].map(d => (
            <button
              type="button" key={d} onClick={() => pushDigit(d)}
              style={{ padding: '20px 0', fontSize: 24, fontWeight: 800, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, cursor: 'pointer', color: 'var(--fg)' }}
            >{d}</button>
          ))}
          <button
            type="button" onClick={backspace}
            style={{ padding: '20px 0', fontSize: 18, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, cursor: 'pointer', color: 'var(--muted)' }}
          >⌫</button>
          <button
            type="button" onClick={() => pushDigit('0')}
            style={{ padding: '20px 0', fontSize: 24, fontWeight: 800, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, cursor: 'pointer', color: 'var(--fg)' }}
          >0</button>
          <button
            type="button" onClick={() => setPin('')}
            style={{ padding: '20px 0', fontSize: 13, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, cursor: 'pointer', color: 'var(--muted)' }}
          >Clear</button>
        </div>

        {err && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, color: '#dc2626', fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
            {err}
          </div>
        )}

        <button
          type="submit" disabled={disabled}
          style={{
            width: '100%', padding: '18px 0', fontSize: 18, fontWeight: 900,
            background: disabled ? 'var(--card-border)' : '#16a34a',
            color: disabled ? 'var(--muted)' : '#fff',
            border: 'none', borderRadius: 10, cursor: disabled ? 'default' : 'pointer',
          }}
        >
          {busy ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

// ── PDI progress bar ─────────────────────────────────────────────────────────
function PdiProgress({ progress }) {
  if (!progress) return null;
  const { answered, total, open_deficiencies, pdi_status } = progress;
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  const done = pdi_status === 'completed';

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>PDI Progress</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {open_deficiencies > 0 && (
            <span className="pill" style={{ background: '#dc2626', color: '#fff' }}>
              {open_deficiencies} open {open_deficiencies === 1 ? 'deficiency' : 'deficiencies'}
            </span>
          )}
          <span style={{ fontSize: 13, color: done ? '#16a34a' : 'var(--muted)', fontWeight: 700 }}>
            {done ? '✓ Complete' : `${pct}%`}
          </span>
        </div>
      </div>
      <div style={{ height: 10, background: 'var(--card-border)', borderRadius: 99 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: done ? '#16a34a' : '#3b82f6', borderRadius: 99, transition: 'width 0.3s' }} />
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{answered} of {total} steps answered</div>
    </div>
  );
}

// ── main portal ───────────────────────────────────────────────────────────────
export default function DriverPortalPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const urlDb     = urlParams.get('db') || '';

  const [dbId,         setDbId]         = useState(() => urlDb || localStorage.getItem('portal_db') || '');
  const [sessionToken, setSessionToken] = useState(() => localStorage.getItem('portal_session') || '');
  const [loggedDriver, setLoggedDriver] = useState(() => {
    try { return JSON.parse(localStorage.getItem('portal_driver_obj') || 'null'); }
    catch { return null; }
  });
  const [query,      setQuery]      = useState('');
  const [scanning,   setScanning]   = useState(false);
  const [scanError,  setScanError]  = useState('');
  const [result,     setResult]     = useState(null);
  const [acting,     setActing]     = useState(false);
  const [actionMsg,  setActionMsg]  = useState('');
  const [actionOk,   setActionOk]   = useState(false);

  // Set the browser tab title while the portal is mounted. The main app uses
  // "Inventory Management", which is confusing for drivers — show them
  // "SNA Portal" instead and restore the original on unmount.
  useEffect(() => {
    const prev = document.title;
    document.title = 'SNA Portal';
    return () => { document.title = prev; };
  }, []);

  // Persist / clear session in storage on change
  useEffect(() => {
    if (sessionToken) localStorage.setItem('portal_session', sessionToken);
    else              localStorage.removeItem('portal_session');
  }, [sessionToken]);
  useEffect(() => {
    if (loggedDriver) localStorage.setItem('portal_driver_obj', JSON.stringify(loggedDriver));
    else              localStorage.removeItem('portal_driver_obj');
  }, [loggedDriver]);

  const logout = () => {
    const t = sessionToken;
    setSessionToken('');
    setLoggedDriver(null);
    setResult(null);
    setQuery('');
    if (t) fetch(API + '/logout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ session_token: t }) }).catch(() => {});
  };

  // OCR / camera state
  const [ocrBusy,       setOcrBusy]       = useState(false);
  const [ocrError,      setOcrError]      = useState('');
  const [ocrCandidates, setOcrCandidates] = useState([]);
  const photoInputRef = useRef(null);

  // Breakdown form state — simplified: just location + reason + photos.
  // Dealer / case / release-driver are admin-only decisions handled from the
  // desktop after dispatch triages the driver's initial report.
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [bLocation, setBLocation]         = useState('');
  const [bReason, setBReason]             = useState('');
  const [bBusy, setBBusy]                 = useState(false);
  const [bErr, setBErr]                   = useState('');
  const [bPhotos, setBPhotos]             = useState([]);                    // pending file objects queued before submit
  const [bUploadingPhotos, setBUploadingPhotos] = useState(false);
  const breakdownPhotoRef = useRef(null);

  // Breakdown notes thread (per-incident). Loaded when the driver taps
  // the "View / add notes" button on the breakdown banner.
  const [bNotesOpen,  setBNotesOpen]  = useState(null);  // breakdown id or null
  const [bNotes,      setBNotes]      = useState([]);
  const [bNotesBusy,  setBNotesBusy]  = useState(false);
  const [bNoteDraft,  setBNoteDraft]  = useState('');

  // Expense form state
  const [expenseOpen, setExpenseOpen]       = useState(false);
  const [expenseMove, setExpenseMove]       = useState(null); // the tile the driver tapped from, or null
  const [expensesListOpen, setExpensesListOpen] = useState(false);
  const [myExpenses, setMyExpenses]         = useState([]);
  const [myExpensesLoading, setMyExpensesLoading] = useState(false);

  // My stats
  const [statsOpen, setStatsOpen]   = useState(false);
  const [myStats, setMyStats]       = useState(null);
  const [myStatsLoading, setMyStatsLoading] = useState(false);

  // Time off
  const [timeOffOpen, setTimeOffOpen]     = useState(false);   // list modal
  const [timeOffForm, setTimeOffForm]     = useState(false);   // request modal
  const [myTimeOff, setMyTimeOff]         = useState([]);
  const [myTimeOffLoading, setMyTimeOffLoading] = useState(false);

  // Company documents + in-app document viewer
  const [companyDocs, setCompanyDocs]     = useState([]);
  const [companyDocsLoading, setCompanyDocsLoading] = useState(false);
  const [viewerDoc, setViewerDoc]         = useState(null); // { url, original_name, mime_type, notes }
  const [docsOpen, setDocsOpen]           = useState(false);

  // Hamburger drawer — replaces the row of header buttons. Drivers with
  // older eyes kept missing the small icons; the hamburger is one big
  // tap target with neon red highlights to stand out on any background.
  const [menuOpen, setMenuOpen]           = useState(false);

  // Force-change-PIN flow. When login returns must_change_pin we stash the
  // temp PIN the driver just typed and show the ChangePin modal. They
  // can't dismiss it — the session token is set but every other screen is
  // blocked until they pick a new PIN.
  const [forceChangePin, setForceChangePin] = useState(null);
    // { tempPin }

  const loadMyTimeOff = async () => {
    setMyTimeOffLoading(true);
    try {
      const res = await fetch(
        `${API}/my-time-off?db=${encodeURIComponent(dbId)}&session_token=${encodeURIComponent(sessionToken)}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      setMyTimeOff(Array.isArray(data.time_off) ? data.time_off : []);
    } catch (_) { /* ignore */ }
    finally { setMyTimeOffLoading(false); }
  };

  const openBreakdownNotes = async (breakdownId) => {
    if (!breakdownId) return;
    setBNotesOpen(breakdownId);
    setBNotes([]);
    setBNoteDraft('');
    setBNotesBusy(true);
    try {
      const res = await fetch(
        `${API}/breakdown/${breakdownId}/notes?db=${encodeURIComponent(dbId)}&session_token=${encodeURIComponent(sessionToken)}`,
        { headers: { 'Content-Type': 'application/json' } });
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      setBNotes(Array.isArray(data.notes) ? data.notes : []);
    } catch (_) { /* ignore */ }
    finally { setBNotesBusy(false); }
  };

  const submitBreakdownNote = async () => {
    const text = bNoteDraft.trim();
    if (!text || !bNotesOpen) return;
    setBNotesBusy(true);
    try {
      const res = await fetch(`${API}/breakdown/${bNotesOpen}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken, db: dbId, note_text: text }),
      });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Note failed');
      setBNoteDraft('');
      // Reload the thread
      const list = await fetch(
        `${API}/breakdown/${bNotesOpen}/notes?db=${encodeURIComponent(dbId)}&session_token=${encodeURIComponent(sessionToken)}`);
      const data = await list.json();
      setBNotes(Array.isArray(data.notes) ? data.notes : []);
    } catch (_) { /* ignore */ }
    finally { setBNotesBusy(false); }
  };

  const loadCompanyDocs = async () => {
    if (!sessionToken || !dbId) return;
    setCompanyDocsLoading(true);
    try {
      const res = await fetch(
        `${API}/documents?db=${encodeURIComponent(dbId)}&session_token=${encodeURIComponent(sessionToken)}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      setCompanyDocs(Array.isArray(data.documents) ? data.documents : []);
    } catch (_) { /* ignore */ }
    finally { setCompanyDocsLoading(false); }
  };

  const loadMyStats = async () => {
    setMyStatsLoading(true);
    try {
      const res = await fetch(
        `${API}/my-stats?db=${encodeURIComponent(dbId)}&session_token=${encodeURIComponent(sessionToken)}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      setMyStats(data);
    } catch (_) { /* ignore */ }
    finally { setMyStatsLoading(false); }
  };

  const loadMyExpenses = async () => {
    setMyExpensesLoading(true);
    try {
      const res = await fetch(
        `${API}/my-expenses?db=${encodeURIComponent(dbId)}&session_token=${encodeURIComponent(sessionToken)}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      setMyExpenses(Array.isArray(data.expenses) ? data.expenses : []);
    } catch (_) { /* ignore */ }
    finally { setMyExpensesLoading(false); }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';   // reset so same file can be re-selected
    setOcrBusy(true);
    setOcrError('');
    setOcrCandidates([]);
    try {
      const form = new FormData();
      form.append('file', file);
      const res  = await fetch('/api/ocr', { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.detail || `OCR error ${res.status}`);
      const cands = data.candidates || [];
      if (cands.length === 0) {
        setOcrError('No VIN or number found in photo — try again or type manually.');
      } else {
        // Try each candidate against the DB — use the first match
        setScanning(true); setScanError(''); setResult(null);
        let matched = false;
        for (const c of cands) {
          try {
            const d = await apiFetch('/scan', { method: 'POST', body: JSON.stringify({ query: c, db: dbId, session_token: sessionToken }) });
            setQuery(c);
            setResult(d);
            matched = true;
            break;
          } catch (_) { /* no match, try next */ }
        }
        if (!matched) {
          setOcrError('No matching record found for any value in the photo — try again or type manually.');
        }
        setScanning(false);
      }
    } catch (err) {
      setOcrError(err.message || 'OCR failed');
    } finally {
      setOcrBusy(false);
    }
  };

  const pickOcrCandidate = (val) => {
    setQuery(val);
    setOcrCandidates([]);
    setOcrError('');
    setScanning(true); setScanError(''); setResult(null);
    apiFetch('/scan', { method: 'POST', body: JSON.stringify({ query: val, db: dbId, session_token: sessionToken }) })
      .then(d => setResult(d))
      .catch(err => {
        if (err.status === 401) { logout(); return; }
        setScanError(err.message || 'Scan failed');
      })
      .finally(() => setScanning(false));
  };

  // Open an assignment tile directly by unit_id — avoids ROWO collisions
  // / case sensitivity / sanitize stripping. Used by the "My Assignments" list.
  const openAssignedUnit = (assignment) => {
    if (!assignment?.unit?.id) return;
    setQuery(assignment.unit.rowo || '');
    setOcrCandidates([]); setOcrError('');
    setScanning(true); setScanError(''); setResult(null);
    apiFetch('/scan', { method: 'POST', body: JSON.stringify({ db: dbId, session_token: sessionToken, unit_id: assignment.unit.id }) })
      .then(d => setResult(d))
      .catch(err => {
        if (err.status === 401) { logout(); return; }
        setScanError(err.message || 'Scan failed');
      })
      .finally(() => setScanning(false));
  };

  useEffect(() => { if (dbId) localStorage.setItem('portal_db', dbId); }, [dbId]);

  const driverName = loggedDriver?.name || '';

  // ── "My Assignments today" — fetched after login, shown above scan form ──
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const reloadAssignments = async () => {
    if (!sessionToken || !dbId) return;
    setLoadingAssignments(true);
    try {
      const res = await fetch(
        `${API}/my-assignments?db=${encodeURIComponent(dbId)}&session_token=${encodeURIComponent(sessionToken)}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 401) { logout(); return; }
      const data = await res.json();
      setAssignments(Array.isArray(data.assignments) ? data.assignments : []);
    } catch (_) { /* ignore */ }
    finally { setLoadingAssignments(false); }
  };

  useEffect(() => {
    if (sessionToken && dbId) {
      reloadAssignments();
      loadCompanyDocs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken, dbId]);

  const handleScan = async (e) => {
    e && e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setScanning(true);
    setScanError('');
    setResult(null);
    setActionMsg('');
    setActionOk(false);
    try {
      const data = await apiFetch('/scan', {
        method: 'POST',
        body: JSON.stringify({ query: q, db: dbId, session_token: sessionToken }),
      });
      setResult(data);
    } catch (err) {
      if (err.status === 401) { logout(); return; }
      setScanError(err.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const openBreakdown = () => {
    setBLocation(''); setBReason(''); setBErr('');
    setBPhotos([]);
    setBreakdownOpen(true);
    // Best-effort: capture GPS so the driver doesn't have to type "I-40 MM 284"
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude.toFixed(5);
          const lng = pos.coords.longitude.toFixed(5);
          setBLocation(`GPS: ${lat}, ${lng}`);
        },
        () => { /* user denied / no permission — leave blank */ },
        { timeout: 4000, maximumAge: 60000 }
      );
    }
  };

  const submitBreakdown = async () => {
    if (!result) return;
    if (!bReason.trim()) { setBErr('Describe what happened.'); return; }
    setBErr(''); setBBusy(true);
    if (bPhotos.length) setBUploadingPhotos(true);
    try {
      // Compress photos before upload — phone JPEGs are 3–10 MB each, which
      // blows past our 8 MB server limit over LTE. compressImages resizes
      // to 1600px and re-encodes as JPEG q=0.82 (~200–500 KB typical).
      const compressed = await compressImages(bPhotos);

      // Single-step multipart submit — location, reason, and all photos go
      // to the server at once, so the notification email includes photos
      // inline instead of firing off before they finish uploading.
      const form = new FormData();
      form.append('unit_id',       String(result.unit.id));
      form.append('action_token',  result.action_token);
      form.append('db',            dbId);
      form.append('session_token', sessionToken);
      form.append('location',      bLocation.trim());
      form.append('reason',        bReason.trim());
      for (const f of compressed) form.append('photos', f);

      const resp = await fetch(API + '/breakdown', { method: 'POST', body: form });
      if (resp.status === 401) { setBreakdownOpen(false); logout(); return; }
      if (!resp.ok) {
        let msg = 'Report failed';
        try { msg = (await resp.json()).error || msg; } catch (_) {}
        throw new Error(msg);
      }

      setBreakdownOpen(false);
      setActionOk(true);
      setActionMsg(`🚨 Breakdown reported${bPhotos.length ? ` with ${bPhotos.length} photo${bPhotos.length === 1 ? '' : 's'}` : ''} — dispatch has been notified.`);
      // Clear so driver goes back to scan screen; dispatch takes over from here.
      setTimeout(() => {
        setResult(null); setQuery(''); setActionOk(false); setActionMsg('');
      }, 3500);
      reloadAssignments();
    } catch (err) {
      setBErr(err.message || 'Report failed');
    } finally {
      setBBusy(false);
      setBUploadingPhotos(false);
    }
  };

  const handleAction = async () => {
    if (!result || !driverName.trim()) return;
    const action = stateToAction(result.state);
    if (!action) return;
    setActing(true);
    setActionMsg('');
    try {
      await apiFetch('/action', {
        method: 'POST',
        body: JSON.stringify({
          action,
          unit_id:      result.unit.id,
          action_token: result.action_token,
          db:           dbId,
          session_token: sessionToken,
        }),
      });
      setActionOk(true);
      setActionMsg(`✓ ${ACTION_LABELS[action]} — recorded for ${driverName.trim()}`);
      setTimeout(() => {
        setResult(null);
        setQuery('');
        setActionOk(false);
        setActionMsg('');
      }, 3500);
    } catch (err) {
      if (err.status === 401) { logout(); return; }
      setActionMsg(err.message || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  if (!dbId) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        <DbSelector onSelect={(id) => setDbId(id)} />
      </div>
    );
  }

  if (!sessionToken || !loggedDriver) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        <DriverLogin
          dbId={dbId}
          onLoggedIn={(token, driver, opts) => {
            setSessionToken(token);
            setLoggedDriver(driver);
            // If dispatch issued a temp PIN, the backend flags
            // must_change_pin and we pop the change-PIN modal here
            // instead of going straight to the landing screen.
            if (opts?.tempPin) setForceChangePin({ tempPin: opts.tempPin });
          }}
          onChangeDb={() => { setDbId(''); localStorage.removeItem('portal_db'); }}
        />
      </div>
    );
  }

  // Gate the rest of the portal until the driver completes the forced
  // PIN change. We keep sessionToken + loggedDriver around so the change
  // endpoint can authenticate the request, but nothing else renders yet.
  if (forceChangePin) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        <ForceChangePinModal
          dbId={dbId}
          sessionToken={sessionToken}
          driverName={loggedDriver?.name || ''}
          tempPin={forceChangePin.tempPin}
          onDone={(fresh) => {
            // Swap in the fresh session token so the clock restarts from
            // the successful PIN change, then drop the gate.
            if (fresh?.session_token) setSessionToken(fresh.session_token);
            if (fresh?.driver) setLoggedDriver(fresh.driver);
            setForceChangePin(null);
          }}
          onCancel={() => {
            // User bailed — log them out so they have to sign in again
            // with the (still-temp) PIN.
            setForceChangePin(null);
            logout();
          }}
        />
      </div>
    );
  }

  const stateMeta  = result ? (STATE_LABELS[result.state] || {}) : null;
  const nextAction = result ? stateToAction(result.state) : null;
  const unit       = result?.unit;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--fg)' }}>

      {/* Header */}
      <div style={{ background: 'var(--card-bg)', borderBottom: '1px solid var(--card-border)', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 20 }}>Driver Portal</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {driverName} · DB: {dbId}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {result && (
            <button
              className="btn"
              style={{ background: '#2563eb', color: '#fff', fontWeight: 800 }}
              onClick={() => { setResult(null); setQuery(''); setActionMsg(''); setActionOk(false); setScanError(''); }}
              title="Return to the landing page"
            >
              🏠 Home
            </button>
          )}
          {/* Neon-red hamburger — replaces the old button row.
              Always-visible single tap target that opens the nav drawer so
              drivers can jump to Stats / Expenses / Time Off / Documents /
              Log out from any screen without backing out first. */}
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            style={{
              width: 48, height: 48, borderRadius: 12,
              background: '#0f172a',
              color: '#ff1744',
              border: '2px solid #ff1744',
              boxShadow: '0 0 0 2px rgba(255,23,68,0.25), 0 0 16px rgba(255,23,68,0.55)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0,
              fontSize: 0,
            }}
          >
            <span style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ width: 22, height: 3, background: '#ff1744', borderRadius: 2, boxShadow: '0 0 6px #ff1744' }} />
              <span style={{ width: 22, height: 3, background: '#ff1744', borderRadius: 2, boxShadow: '0 0 6px #ff1744' }} />
              <span style={{ width: 22, height: 3, background: '#ff1744', borderRadius: 2, boxShadow: '0 0 6px #ff1744' }} />
            </span>
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px 16px' }}>

        {/* Big visible "who am I" banner — drivers have asked for confirmation
            they're correctly logged in. */}
        {!result && (
          <div style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: '#ffffff',
            borderRadius: 12,
            padding: '18px 20px',
            marginBottom: 12,
            boxShadow: '0 4px 12px rgba(22,163,74,0.2)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.85 }}>
              Signed in as
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4, lineHeight: 1.1 }}>
              {driverName}
            </div>
            {loggedDriver?.home_terminal && (
              <div style={{ fontSize: 13, marginTop: 4, opacity: 0.95 }}>
                🏠 {loggedDriver.home_terminal}
              </div>
            )}
          </div>
        )}

        {/* My Assignments Today */}
        {!result && assignments.length > 0 && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 17 }}>Your Assignments</div>
              <button className="btn-outline" style={{ padding: '4px 10px', fontSize: 12 }} onClick={reloadAssignments} disabled={loadingAssignments}>
                {loadingAssignments ? '…' : 'Refresh'}
              </button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
              Tap an assignment to open it directly, or scan a VIN below.
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {assignments.map((a) => {
                if (!a.unit) return null;
                const brokeDown = a.unit.broke_down;
                return (
                  <div
                    key={a.trip_id}
                    style={{
                      background: brokeDown ? '#fef2f2' : 'var(--card-bg)',
                      border: `1px solid ${brokeDown ? '#fca5a5' : 'var(--card-border)'}`,
                      borderRadius: 10, color: 'var(--fg)', overflow: 'hidden',
                    }}
                  >
                    <button
                      onClick={() => openAssignedUnit(a)}
                      style={{
                        width: '100%', textAlign: 'left', padding: 12, border: 'none',
                        background: 'transparent', cursor: 'pointer', color: 'var(--fg)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 800, fontSize: 15 }}>
                          {brokeDown && '🚨 '}{a.unit.rowo || `#${a.unit.id}`}
                          {a.unit.stock_po && <span style={{ opacity: 0.6, fontWeight: 500 }}> · {a.unit.stock_po}</span>}
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 99, background: STATE_LABELS[a.unit.state]?.bg, color: STATE_LABELS[a.unit.state]?.color }}>
                          {STATE_LABELS[a.unit.state]?.label || a.unit.state}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, marginTop: 4, opacity: 0.75 }}>
                        Move #{a.trip_id}{a.destination ? ` → ${a.destination}` : ''}
                        {a.start_date ? ` · ${a.start_date}` : ''}
                        {a.move_type === 'sna_to_customer' ? ' · SNA → Customer' : ''}
                      </div>
                      {a.unit.customer && (
                        <div style={{ fontSize: 12, marginTop: 2, opacity: 0.6 }}>For: {a.unit.customer}</div>
                      )}
                    </button>
                    {Array.isArray(a.attachments) && a.attachments.length > 0 && (
                      <div style={{
                        padding: '10px 12px',
                        borderTop: '1px solid var(--card-border)',
                        background: 'rgba(29,78,216,0.06)',
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: '#1d4ed8', marginBottom: 6 }}>
                          📎 Documents ({a.attachments.length})
                        </div>
                        <div style={{ display: 'grid', gap: 6 }}>
                          {a.attachments.map((att) => {
                            const kindLabel = att.kind === 'airline_ticket' ? '✈️ Ticket'
                                            : att.kind === 'permit'         ? '📄 Permit'
                                            : '📎 Doc';
                            return (
                              <button
                                key={att.id}
                                type="button"
                                onClick={() => setViewerDoc(att)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                                  padding: '8px 10px', borderRadius: 8,
                                  background: '#fff', border: '1px solid #bfdbfe',
                                  color: '#1d4ed8', fontSize: 13, textAlign: 'left', cursor: 'pointer',
                                }}
                              >
                                <span style={{ fontWeight: 800 }}>{kindLabel}</span>
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {att.original_name}
                                </span>
                                <span style={{ fontSize: 11, opacity: 0.7 }}>View ↗</span>
                                {att.notes && (
                                  <div style={{ flexBasis: '100%', fontSize: 11, color: '#475569', marginTop: 2 }}>
                                    {att.notes}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => { setExpenseMove(a); setExpenseOpen(true); }}
                      style={{
                        width: '100%', padding: '10px 12px', fontSize: 13, fontWeight: 700,
                        border: 'none', borderTop: '1px solid var(--card-border)',
                        background: 'rgba(147,51,234,0.08)', color: '#9333ea', cursor: 'pointer',
                      }}
                    >
                      🧾 Add expense for this move
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!result && assignments.length === 0 && !loadingAssignments && (
          <div className="card" style={{ marginBottom: 12, background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>No active assignments</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              You have no moves assigned right now. Contact operations if you're expecting work.
            </div>
          </div>
        )}


        {/* Big, obvious Add Expense button — works even without an active move.
            Drivers are older; this button is intentionally huge + high-contrast. */}
        {!result && (
          <button
            onClick={() => { setExpenseMove(null); setExpenseOpen(true); }}
            style={{
              width: '100%',
              marginBottom: 12,
              padding: '20px 0',
              fontSize: 22,
              fontWeight: 900,
              background: '#9333ea',
              color: '#ffffff',
              border: 'none',
              borderRadius: 12,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(147,51,234,0.25)',
            }}
          >
            🧾 Add Expense / Receipt
          </button>
        )}

        {/* Scan form */}
        {!result && (
          <div className="card">
            <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 4 }}>Scan a Unit</div>
            <div className="muted" style={{ marginBottom: 20, fontSize: 15 }}>Enter a VIN, ROWO, or Stock/PO number</div>

            {/* Hidden file input — opens camera on mobile */}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handlePhotoCapture}
            />

            {/* Camera button */}
            <button
              type="button"
              onClick={() => { setOcrError(''); setOcrCandidates([]); photoInputRef.current?.click(); }}
              disabled={ocrBusy || scanning}
              style={{
                width: '100%',
                padding: '18px 0',
                fontSize: 18,
                fontWeight: 800,
                marginBottom: 10,
                background: ocrBusy ? 'var(--card-border)' : '#1d4ed8',
                color: ocrBusy ? 'var(--muted)' : '#fff',
                border: 'none',
                borderRadius: 10,
                cursor: ocrBusy ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              {ocrBusy
                ? <><span style={{ display: 'inline-block', width: 20, height: 20, border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Reading photo…</>
                : <><span style={{ fontSize: 24 }}>📷</span> Take Photo of VIN / Label</>
              }
            </button>

            {/* OCR error */}
            {ocrError && (
              <div style={{ marginBottom: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', color: '#92400e', fontSize: 14, fontWeight: 600 }}>
                {ocrError}
              </div>
            )}

            {/* OCR multi-candidate picker */}
            {ocrCandidates.length > 1 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, color: 'var(--fg)' }}>
                  Multiple values found — tap to select:
                </div>
                {ocrCandidates.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => pickOcrCandidate(c)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 14px',
                      marginBottom: 6,
                      background: 'var(--card-bg)',
                      border: '1px solid var(--card-border)',
                      borderRadius: 8,
                      fontFamily: 'monospace',
                      fontSize: 16,
                      fontWeight: 700,
                      cursor: 'pointer',
                      color: 'var(--fg)',
                    }}
                  >
                    {c}
                    {c.length === 17 && <span style={{ marginLeft: 8, fontSize: 11, color: '#16a34a', fontFamily: 'sans-serif' }}>17-char VIN</span>}
                  </button>
                ))}
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Or type manually below</div>
              </div>
            )}

            <form onSubmit={handleScan}>
              <input
                className="input"
                style={{ width: '100%', padding: '16px', fontSize: 20, marginBottom: 12, boxSizing: 'border-box', fontFamily: 'monospace' }}
                placeholder="VIN / ROWO / Stock PO…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoCapitalize="characters"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                autoFocus
              />
              <button
                type="submit"
                className="btn"
                disabled={scanning || !query.trim()}
                style={{ width: '100%', padding: '18px 0', fontSize: 20, fontWeight: 900 }}
              >
                {scanning ? 'Searching…' : 'Search →'}
              </button>
            </form>

            {scanError && (
              <div style={{ marginTop: 14, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', color: '#dc2626', fontWeight: 700, fontSize: 15 }}>
                {scanError}
              </div>
            )}
          </div>
        )}

        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>

        {/* Result card */}
        {result && unit && (
          <div>
            {/* State badge + scan again */}
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ background: stateMeta?.bg, color: stateMeta?.color, borderRadius: 99, padding: '6px 16px', fontWeight: 800, fontSize: 15 }}>
                {stateMeta?.label}
              </div>
              <button
                className="btn-outline"
                onClick={() => { setResult(null); setQuery(''); setActionMsg(''); setActionOk(false); }}
              >
                ← Scan Again
              </button>
            </div>

            {/* Breakdown banner — open incident takes priority over normal state */}
            {result.latest_breakdown && result.latest_breakdown.status === 'open' && (
              <div style={{ background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: 12, padding: 14, marginBottom: 12 }}>
                <div style={{ fontWeight: 900, fontSize: 15, color: '#991b1b' }}>🚨 BROKEN DOWN</div>
                <div style={{ fontSize: 13, color: '#7f1d1d', marginTop: 4, lineHeight: 1.5 }}>
                  {result.latest_breakdown.location && <div><strong>Location:</strong> {result.latest_breakdown.location}</div>}
                  {result.latest_breakdown.svcdealer && <div><strong>Dealer:</strong> {result.latest_breakdown.svcdealer}</div>}
                  {result.latest_breakdown.case_vendor && <div><strong>Vendor:</strong> {result.latest_breakdown.case_vendor}{result.latest_breakdown.casenumber ? ` · Case #${result.latest_breakdown.casenumber}` : ''}</div>}
                  {result.latest_breakdown.reason && <div style={{ fontStyle: 'italic', marginTop: 4 }}>"{result.latest_breakdown.reason}"</div>}
                </div>
                <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 8 }}>
                  Dispatch will mark this resolved when the repair is confirmed.
                </div>
                <button
                  type="button"
                  onClick={() => openBreakdownNotes(result.latest_breakdown.id)}
                  style={{
                    marginTop: 10, width: '100%', padding: '10px 12px', fontSize: 14, fontWeight: 800,
                    background: '#991b1b', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
                  }}
                >
                  💬 View / add notes
                </button>
              </div>
            )}

            {/* Active move banner — shows the driver why they're here */}
            {result.active_move && (
              <div style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Active Move #{result.active_move.trip_id}{result.active_move.move_type === 'sna_to_customer' ? ' · SNA → Customer' : ' · Factory → SNA'}
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#1e3a8a', marginTop: 2 }}>
                  → {result.active_move.destination || '(no destination)'}
                </div>
                {result.active_move.driver_name && (
                  <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 2 }}>Assigned: {result.active_move.driver_name}</div>
                )}
              </div>
            )}

            {/* Unit info */}
            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 900, fontSize: 22, marginBottom: 2 }}>
                {unit.rowo ? `RO/WO: ${unit.rowo}` : unit.stock_po ? `Stock/PO: ${unit.stock_po}` : `Unit #${unit.id}`}
              </div>
              {(unit.customer || unit.type) && (
                <div className="muted" style={{ fontSize: 14, marginBottom: 12 }}>
                  {[unit.customer, unit.type].filter(Boolean).join(' · ')}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                {[
                  ['Stock / PO',       unit.stock_po],
                  ['Body Make/Model',   unit.body_make_model],
                  ['Chassis Make/Model',unit.chassis_make_model],
                  ['Body VIN',          unit.body_vin],
                  ['Chassis VIN',       unit.chassis_vin],
                  ['Status',            unit.unit_status],
                  ['Location',          unit.unit_location],
                ].filter(([, v]) => v).map(([label, val]) => (
                  <div key={label}>
                    <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Timeline */}
              <div style={{ marginTop: 16, borderTop: '1px solid var(--card-border)', paddingTop: 14, display: 'grid', gap: 6 }}>
                {[
                  ['Ready at Factory',      unit.ready_at_factory],
                  ['Factory Pickup',        unit.factory_pickup_at],
                  ['Arrived at SNA',        unit.arrive_sna],
                  ['PDI Started',           unit.pdi_started_at],
                  ['PDI Complete',          unit.pdi_completed_at],
                  ['Delivered to Customer', unit.to_customer],
                ].map(([label, val]) => (
                  <div key={label} className="row" style={{ gap: 10, alignItems: 'center' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: val ? '#16a34a' : 'var(--card-border)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: val ? 'var(--fg)' : 'var(--muted)', fontWeight: val ? 700 : 400 }}>
                      {label}{val ? `: ${val.slice(0, 16).replace('T', ' ')}` : ''}
                    </span>
                  </div>
                ))}
                {unit.portal_driver_name && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Last updated by: {unit.portal_driver_name}</div>
                )}
              </div>
            </div>

            {/* PDI Progress */}
            <PdiProgress progress={result.pdi_progress} />

            {/* Action section — gated on assigned_to_me */}
            {!result.assigned_to_me ? (
              <div style={{ background: '#fef3c7', border: '2px solid #fde68a', borderRadius: 12, padding: 20, marginTop: 12 }}>
                <div style={{ fontSize: 32, marginBottom: 8, textAlign: 'center' }}>📞</div>
                <div style={{ fontWeight: 900, fontSize: 18, color: '#92400e', textAlign: 'center' }}>Not Assigned to You</div>
                <div style={{ color: '#b45309', marginTop: 8, fontSize: 14, textAlign: 'center', lineHeight: 1.5 }}>
                  This unit isn't on your active move list. Please <strong>call operations</strong> before doing anything with it.
                </div>
              </div>
            ) : nextAction ? (
              <div className="card" style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 12 }}>Log Action as {driverName}</div>
                <button
                  className="btn"
                  onClick={handleAction}
                  disabled={acting}
                  style={{
                    width: '100%',
                    padding: '20px 0',
                    fontSize: 20,
                    fontWeight: 900,
                    background: acting ? 'var(--card-border)' : '#16a34a',
                    color: acting ? 'var(--muted)' : '#fff',
                  }}
                >
                  {acting ? 'Saving…' : ACTION_LABELS[nextAction]}
                </button>
              </div>
            ) : result.state === 'delivered' ? (
              <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: 12, padding: 20, marginTop: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✓</div>
                <div style={{ fontWeight: 900, fontSize: 20, color: '#15803d' }}>Unit Delivered</div>
                <div style={{ color: '#16a34a', marginTop: 4 }}>This unit has been delivered to the customer. All steps complete.</div>
              </div>
            ) : (result.state === 'at_sna' || result.state === 'pdi_in_progress') ? (
              <div style={{ background: '#fef3c7', border: '2px solid #fde68a', borderRadius: 12, padding: 20, marginTop: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔧</div>
                <div style={{ fontWeight: 900, fontSize: 18, color: '#92400e' }}>
                  {result.state === 'pdi_in_progress' ? 'PDI in Progress' : 'Awaiting PDI'}
                </div>
                <div style={{ color: '#b45309', marginTop: 4, fontSize: 14 }}>
                  This unit cannot be delivered until PDI is complete. PDI is managed in the technician app.
                </div>
              </div>
            ) : null}

            {/* Report Breakdown — only if this unit is actually assigned to the
                logged-in driver. Hidden if already broken down or delivered. */}
            {result.assigned_to_me && (!result.latest_breakdown || result.latest_breakdown.status !== 'open') && result.state !== 'delivered' && (
              <button
                onClick={openBreakdown}
                style={{
                  width: '100%',
                  marginTop: 12,
                  padding: '14px 0',
                  fontSize: 16,
                  fontWeight: 800,
                  background: '#fef2f2',
                  color: '#dc2626',
                  border: '2px solid #fca5a5',
                  borderRadius: 10,
                  cursor: 'pointer',
                }}
              >
                🚨 Report Breakdown
              </button>
            )}

            {/* Action result */}
            {actionMsg && (
              <div style={{ marginTop: 12, background: actionOk ? '#f0fdf4' : '#fef2f2', border: `2px solid ${actionOk ? '#86efac' : '#fca5a5'}`, borderRadius: 10, padding: '14px 18px', fontWeight: 700, fontSize: 16, color: actionOk ? '#15803d' : '#dc2626', textAlign: 'center' }}>
                {actionMsg}
              </div>
            )}
          </div>
        )}

        {/* Report Breakdown modal — simple by design (drivers at roadside,
            one-handed phone use). Dispatch fills in finer detail later. */}
        {breakdownOpen && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
            onClick={(e) => { if (e.target === e.currentTarget) setBreakdownOpen(false); }}
          >
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14, width: '100%', maxWidth: 460, maxHeight: '92vh', overflowY: 'auto', padding: 20 }}>
              <div style={{ fontWeight: 900, fontSize: 22, color: '#dc2626', marginBottom: 4 }}>🚨 Report Breakdown</div>
              <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
                Unit {result?.unit?.rowo || `#${result?.unit?.id}`}{result?.unit?.stock_po ? ` · Stock/PO ${result.unit.stock_po}` : ''}
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Location *</div>
                  <input
                    className="input" style={{ width: '100%', padding: 12, fontSize: 15, boxSizing: 'border-box' }}
                    placeholder="e.g. I-40 MM 284, Amarillo TX"
                    value={bLocation} onChange={e => setBLocation(e.target.value)}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>What happened? *</div>
                  <textarea
                    className="input" style={{ width: '100%', padding: 12, fontSize: 15, minHeight: 80, boxSizing: 'border-box' }}
                    placeholder="Check engine, loss of power, towed to dealer…"
                    value={bReason} onChange={e => setBReason(e.target.value)}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Photos (optional)</div>
                  <input
                    ref={breakdownPhotoRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []).slice(0, 8);
                      setBPhotos(prev => [...prev, ...files].slice(0, 8));
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => breakdownPhotoRef.current?.click()}
                    style={{
                      width: '100%', padding: 12, fontSize: 14, fontWeight: 700,
                      background: 'var(--card-bg)', border: '1px dashed var(--card-border)',
                      borderRadius: 8, cursor: 'pointer', color: 'var(--fg)',
                    }}
                  >
                    📷 Add photos ({bPhotos.length}/8)
                  </button>
                  {bPhotos.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                      {bPhotos.map((f, i) => (
                        <div key={i} style={{ position: 'relative', width: 64, height: 64, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--card-border)' }}>
                          <img src={URL.createObjectURL(f)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <button
                            type="button"
                            onClick={() => setBPhotos(prev => prev.filter((_, j) => j !== i))}
                            style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: 1 }}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="muted" style={{ fontSize: 12 }}>Reporting as <strong>{driverName}</strong></div>

                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 10, fontSize: 12, color: '#1e3a8a' }}>
                  ℹ️ Dispatch will call you after the report and decide whether to hold you with the unit or release you for the day — no action needed from you.
                </div>

                {bErr && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 10, color: '#dc2626', fontWeight: 700, fontSize: 14 }}>
                    {bErr}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button
                  className="btn-outline"
                  style={{ flex: 1, padding: '14px 0', fontSize: 15 }}
                  onClick={() => setBreakdownOpen(false)}
                  disabled={bBusy}
                >
                  Cancel
                </button>
                <button
                  onClick={submitBreakdown}
                  disabled={bBusy}
                  style={{
                    flex: 2, padding: '14px 0', fontSize: 16, fontWeight: 900,
                    background: bBusy ? 'var(--card-border)' : '#dc2626',
                    color: '#fff', border: 'none', borderRadius: 10, cursor: bBusy ? 'default' : 'pointer',
                  }}
                >
                  {bBusy ? (bUploadingPhotos ? 'Uploading photos…' : 'Reporting…') : 'Send Breakdown Report'}
                </button>
              </div>
              <div className="muted" style={{ fontSize: 11, marginTop: 10, textAlign: 'center' }}>
                Dispatch gets notified immediately with your report.
              </div>
            </div>
          </div>
        )}

        {/* Add Expense modal */}
        {expenseOpen && (
          <ExpenseModal
            dbId={dbId}
            sessionToken={sessionToken}
            assignment={expenseMove}
            onClose={() => setExpenseOpen(false)}
            onSubmitted={() => {
              setExpenseOpen(false);
              setActionOk(true);
              setActionMsg('🧾 Expense submitted — accounting has been notified.');
              setTimeout(() => { setActionOk(false); setActionMsg(''); }, 3500);
              if (expensesListOpen) loadMyExpenses();
            }}
            onAuthError={() => { setExpenseOpen(false); logout(); }}
          />
        )}

        {/* My Expenses list */}
        {expensesListOpen && (
          <MyExpensesModal
            expenses={myExpenses}
            loading={myExpensesLoading}
            onRefresh={loadMyExpenses}
            onClose={() => setExpensesListOpen(false)}
          />
        )}

        {/* My Stats */}
        {statsOpen && (
          <MyStatsModal
            stats={myStats}
            loading={myStatsLoading}
            driverName={driverName}
            onRefresh={loadMyStats}
            onClose={() => setStatsOpen(false)}
          />
        )}

        {/* Time Off list + request */}
        {timeOffOpen && (
          <TimeOffListModal
            entries={myTimeOff}
            loading={myTimeOffLoading}
            onRequest={() => setTimeOffForm(true)}
            onRefresh={loadMyTimeOff}
            onDelete={async (id) => {
              if (!window.confirm("Cancel this time-off request?")) return;
              try {
                const res = await fetch(`${API}/time-off/${id}`, {
                  method: 'DELETE',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ db: dbId, session_token: sessionToken }),
                });
                if (res.status === 401) { logout(); return; }
                await loadMyTimeOff();
              } catch (_) {}
            }}
            onClose={() => setTimeOffOpen(false)}
          />
        )}
        {timeOffForm && (
          <TimeOffRequestModal
            dbId={dbId}
            sessionToken={sessionToken}
            onClose={() => setTimeOffForm(false)}
            onSubmitted={() => {
              setTimeOffForm(false);
              setActionOk(true);
              setActionMsg('📅 Time off submitted — dispatch has been notified.');
              setTimeout(() => { setActionOk(false); setActionMsg(''); }, 3500);
              if (timeOffOpen) loadMyTimeOff();
            }}
            onAuthError={() => { setTimeOffForm(false); logout(); }}
          />
        )}

        {menuOpen && (
          <DriverPortalMenu
            driverName={driverName}
            terminal={loggedDriver?.home_terminal || ''}
            onClose={() => setMenuOpen(false)}
            onPick={(key) => {
              setMenuOpen(false);
              // Small delay so the drawer-close animation reads before
              // the destination modal slides in on top.
              setTimeout(() => {
                if (key === 'home') {
                  setResult(null); setQuery(''); setActionMsg(''); setActionOk(false); setScanError('');
                } else if (key === 'stats')    { loadMyStats();     setStatsOpen(true); }
                else if (key === 'expenses')   { loadMyExpenses();  setExpensesListOpen(true); }
                else if (key === 'time_off')   { loadMyTimeOff();   setTimeOffOpen(true); }
                else if (key === 'documents')  { loadCompanyDocs(); setDocsOpen(true); }
                else if (key === 'logout')     { logout(); }
              }, 60);
            }}
          />
        )}

        {docsOpen && (
          <DriverDocumentsPanel
            documents={companyDocs}
            loading={companyDocsLoading}
            onRefresh={loadCompanyDocs}
            onOpenDoc={(d) => setViewerDoc(d)}
            onClose={() => setDocsOpen(false)}
          />
        )}

        {viewerDoc && (
          <DocumentViewer doc={viewerDoc} onClose={() => setViewerDoc(null)} />
        )}

        {bNotesOpen && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(15,23,42,0.75)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}
            onClick={(e) => { if (e.target === e.currentTarget) setBNotesOpen(null); }}
          >
            <div style={{
              background: 'var(--card-bg)', color: 'var(--fg)', borderTopLeftRadius: 18, borderTopRightRadius: 18,
              width: '100%', maxWidth: 520, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 -10px 30px rgba(0,0,0,0.25)',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px',
                borderBottom: '1px solid var(--card-border)',
              }}>
                <div style={{ fontWeight: 900, fontSize: 17 }}>💬 Breakdown Notes</div>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={() => setBNotesOpen(null)}
                  style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 800, cursor: 'pointer' }}>
                  ✕ Close
                </button>
              </div>

              <div style={{ padding: '12px 18px', overflowY: 'auto', flex: 1 }}>
                {bNotesBusy && bNotes.length === 0 ? (
                  <div className="muted">Loading…</div>
                ) : bNotes.length === 0 ? (
                  <div className="muted" style={{ fontSize: 14 }}>No notes yet — add the first one below so dispatch has your current status.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {bNotes.map((n) => {
                      const isDriver = n.author_type === 'driver';
                      return (
                        <div key={n.id} style={{
                          background: isDriver ? '#eff6ff' : '#fffbeb',
                          border: `1px solid ${isDriver ? '#bfdbfe' : '#fde68a'}`,
                          borderRadius: 10, padding: '10px 12px',
                        }}>
                          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
                            color: isDriver ? '#1d4ed8' : '#b45309' }}>
                            {isDriver ? '👤 Driver' : '🧑‍💼 Dispatch'}
                            {n.author_name && ` · ${n.author_name}`}
                            {n.created_at && ` · ${String(n.created_at).slice(0, 16).replace('T', ' ')}`}
                          </div>
                          <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', fontSize: 14, color: '#111827' }}>
                            {n.note_text}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ padding: '12px 18px', borderTop: '1px solid var(--card-border)' }}>
                <textarea
                  className="input"
                  placeholder="Add a note — where you are, what's happening, anything dispatch should know…"
                  value={bNoteDraft}
                  onChange={(e) => setBNoteDraft(e.target.value)}
                  style={{ width: '100%', minHeight: 70, fontSize: 14, padding: 10, boxSizing: 'border-box' }}
                />
                <button
                  type="button"
                  onClick={submitBreakdownNote}
                  disabled={bNotesBusy || !bNoteDraft.trim()}
                  style={{
                    marginTop: 8, width: '100%', padding: '12px 0', fontSize: 15, fontWeight: 800,
                    background: bNotesBusy || !bNoteDraft.trim() ? 'var(--card-border)' : '#1d4ed8',
                    color: '#fff', border: 'none', borderRadius: 10,
                    cursor: bNotesBusy || !bNoteDraft.trim() ? 'default' : 'pointer',
                  }}
                >
                  {bNotesBusy ? 'Saving…' : 'Post Note'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── In-app document viewer ────────────────────────────────────────────────
// Big, friendly close/print/share controls. Works for PDFs (iframe), images
// (<img>), and falls back to a download link for other types (.doc, .xls,
// etc) so the driver can always reach the file. On mobile, the Share button
// uses the Web Share API when available to hand the file off to email,
// WhatsApp, Messages, etc; otherwise it offers a mailto fallback.
function DocumentViewer({ doc, onClose }) {
  const mt = String(doc.mime_type || '').toLowerCase();
  const isPdf   = mt === 'application/pdf' || /\.pdf$/i.test(doc.original_name || doc.url || '');
  const isImage = mt.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(doc.original_name || doc.url || '');

  // Close on Escape for keyboard users.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const absUrl = () => {
    try { return new URL(doc.url, window.location.origin).toString(); }
    catch { return doc.url; }
  };

  const handlePrint = async () => {
    // Opening the file in a new tab and calling its window.print() is the
    // most reliable cross-browser path: printing an iframe often hits CORS
    // or same-origin quirks in mobile Safari. The driver just taps the
    // browser's print button from there.
    const w = window.open(absUrl(), '_blank');
    if (!w) { alert('Please allow pop-ups to print.'); return; }
    try {
      w.addEventListener('load', () => { try { w.focus(); w.print(); } catch (_) {} });
    } catch (_) { /* the tab is open — driver can still print manually */ }
  };

  const handleShare = async () => {
    const url = absUrl();
    const title = doc.label || doc.original_name || 'Document';
    // Prefer Web Share API with file if we can fetch the blob — gives a
    // real attachment in WhatsApp / Mail etc.
    if (navigator.share) {
      try {
        // File-level share when supported.
        if (navigator.canShare) {
          try {
            const resp = await fetch(url);
            const blob = await resp.blob();
            const file = new File([blob], doc.original_name || 'document', { type: blob.type });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({ title, files: [file] });
              return;
            }
          } catch (_) { /* fall through to URL share */ }
        }
        await navigator.share({ title, url });
        return;
      } catch (_) { /* user cancelled or API failed — try mailto */ }
    }
    // Fallback: mailto with the URL.
    const subject = encodeURIComponent(title);
    const body    = encodeURIComponent(`Document link:\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = doc.url;
    a.download = doc.original_name || 'document';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.92)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Top bar — close + title + actions. Always visible, always tappable. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
        background: '#0f172a', color: '#fff', borderBottom: '1px solid #1e293b',
        flexWrap: 'wrap',
      }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 14px', fontWeight: 800, fontSize: 15, cursor: 'pointer',
          }}
        >
          ✕ Close
        </button>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 700 }}>
          {doc.label || doc.original_name || 'Document'}
        </div>
        <button type="button" onClick={handlePrint} style={viewerBtnStyle}>🖨 Print</button>
        <button type="button" onClick={handleShare} style={viewerBtnStyle}>↗ Share</button>
        <button type="button" onClick={handleDownload} style={viewerBtnStyle}>⬇ Save</button>
      </div>

      {/* Body — embed the file inline when we can. */}
      <div style={{ flex: 1, background: '#1e293b', overflow: 'auto' }}>
        {isPdf ? (
          <iframe
            title={doc.original_name || 'Document'}
            src={doc.url}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        ) : isImage ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100%', padding: 12 }}>
            <img
              src={doc.url}
              alt={doc.original_name || ''}
              style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
            />
          </div>
        ) : (
          <div style={{ padding: 24, color: '#fff', textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Preview not available</div>
            <div style={{ opacity: 0.8, fontSize: 14, marginBottom: 16 }}>
              This file type can't be shown inline. Tap <strong>Save</strong> to download or <strong>Share</strong> to send it.
            </div>
            <button type="button" onClick={handleDownload} style={{ ...viewerBtnStyle, background: '#16a34a' }}>
              ⬇ Download {doc.original_name}
            </button>
          </div>
        )}
      </div>

      {doc.notes && (
        <div style={{ padding: '10px 14px', background: '#0f172a', color: '#cbd5e1', borderTop: '1px solid #1e293b', fontSize: 13 }}>
          {doc.notes}
        </div>
      )}
    </div>
  );
}
const viewerBtnStyle = {
  background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 14px', fontWeight: 800, fontSize: 15, cursor: 'pointer',
};

// ── Expense entry modal (big fonts, camera, tied to active move) ───────────
const EXPENSE_CATEGORIES = [
  { key: 'fuel',    label: 'Fuel',    icon: '⛽' },
  { key: 'meal',    label: 'Meal',    icon: '🍔' },
  { key: 'hotel',   label: 'Hotel',   icon: '🏨' },
  { key: 'tolls',   label: 'Tolls',   icon: '🛣️' },
  { key: 'repair',  label: 'Repair',  icon: '🔧' },
  { key: 'parking', label: 'Parking', icon: '🅿️' },
  { key: 'other',   label: 'Other',   icon: '📎' },
];

function ExpenseModal({ dbId, sessionToken, assignment, onClose, onSubmitted, onAuthError }) {
  const [category, setCategory]       = useState('fuel');
  const [amount, setAmount]           = useState('');
  const [currency, setCurrency]       = useState('CAD');
  const [vendor, setVendor]           = useState('');
  const [expenseDate, setExpenseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes]             = useState('');
  const [photos, setPhotos]           = useState([]);
  const [busy, setBusy]               = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [err, setErr]                 = useState('');
  const photoRef = useRef(null);

  const submit = async () => {
    setErr('');
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) return setErr('Enter a valid amount.');
    if (!expenseDate) return setErr('Pick the expense date.');
    setBusy(true);
    try {
      const res = await fetch('/api/portal/expenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          db: dbId, session_token: sessionToken,
          category, amount: amt, currency, vendor,
          expense_date: expenseDate, notes,
          inventory_id: assignment?.unit?.id || null,
          move_id:      assignment?.trip_id || null,
        }),
      });
      if (res.status === 401) return onAuthError?.();
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      const id = data.expense?.id;
      if (id && photos.length) {
        setUploading(true);
        // Compress receipts to keep upload fast on LTE. 1600px + q=0.82
        // keeps receipt text readable while trimming a 6 MB phone photo
        // down to a few hundred KB.
        const compressed = await compressImages(photos);
        for (const f of compressed) {
          try {
            const form = new FormData();
            form.append('file', f);
            form.append('session_token', sessionToken);
            form.append('db', dbId);
            await fetch(`/api/portal/expenses/${id}/photo`, { method: 'POST', body: form });
          } catch (_) { /* skip */ }
        }
        setUploading(false);
      }
      onSubmitted?.();
    } catch (ex) {
      setErr(ex.message || 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  const bigInput = { width: '100%', padding: 16, fontSize: 20, boxSizing: 'border-box' };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14, width: '100%', maxWidth: 500, maxHeight: '94vh', overflowY: 'auto', padding: 20 }}>
        <div style={{ fontWeight: 900, fontSize: 24, marginBottom: 4, color: '#9333ea' }}>🧾 New Expense</div>
        {assignment?.unit ? (
          <div className="muted" style={{ fontSize: 14, marginBottom: 16 }}>
            For: <strong>{assignment.unit.rowo || `#${assignment.unit.id}`}</strong>
            {assignment.unit.stock_po ? ` · ${assignment.unit.stock_po}` : ''} · Move #{assignment.trip_id}
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 14, marginBottom: 16 }}>
            Not tied to a specific unit — accounting will categorize.
          </div>
        )}

        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Category</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
              {EXPENSE_CATEGORIES.map(c => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  style={{
                    padding: '14px 4px',
                    background: category === c.key ? '#9333ea' : 'var(--card-bg)',
                    color:      category === c.key ? '#fff'    : 'var(--fg)',
                    border:     `2px solid ${category === c.key ? '#9333ea' : 'var(--card-border)'}`,
                    borderRadius: 10, cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}
                >
                  <div style={{ fontSize: 24 }}>{c.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>{c.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Amount *</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={currency} onChange={e => setCurrency(e.target.value)}
                className="input"
                style={{ flex: '0 0 90px', padding: 16, fontSize: 18 }}
              >
                <option>CAD</option>
                <option>USD</option>
              </select>
              <input
                className="input" type="number" inputMode="decimal" placeholder="0.00"
                step="0.01" min="0"
                value={amount} onChange={e => setAmount(e.target.value)}
                style={{ ...bigInput, flex: 1, fontWeight: 800 }}
                autoFocus
              />
            </div>
          </div>

          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Date *</div>
            <input type="date" className="input"
              value={expenseDate} onChange={e => setExpenseDate(e.target.value)}
              style={bigInput}
            />
          </div>

          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Vendor</div>
            <input className="input" placeholder="e.g. Petro Canada"
              value={vendor} onChange={e => setVendor(e.target.value)}
              style={bigInput}
            />
          </div>

          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Notes (optional)</div>
            <textarea className="input"
              value={notes} onChange={e => setNotes(e.target.value)}
              style={{ ...bigInput, minHeight: 70, fontSize: 16 }}
              placeholder="Anything accounting should know"
            />
          </div>

          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Receipt Photo(s)</div>
            <input
              ref={photoRef} type="file" accept="image/*" capture="environment" multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []).slice(0, 5);
                setPhotos(prev => [...prev, ...files].slice(0, 5));
                e.target.value = '';
              }}
            />
            <button type="button" onClick={() => photoRef.current?.click()}
              style={{
                width: '100%', padding: 16, fontSize: 17, fontWeight: 800,
                background: '#1d4ed8', color: '#fff',
                border: 'none', borderRadius: 10, cursor: 'pointer',
              }}
            >
              📷 Take Photo of Receipt ({photos.length}/5)
            </button>
            {photos.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {photos.map((f, i) => (
                  <div key={i} style={{ position: 'relative', width: 80, height: 80, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--card-border)' }}>
                    <img src={URL.createObjectURL(f)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}
                      style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 14, cursor: 'pointer', lineHeight: 1 }}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {err && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, color: '#dc2626', fontWeight: 700, fontSize: 15 }}>
              {err}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button onClick={onClose} disabled={busy}
            style={{ flex: 1, padding: '18px 0', fontSize: 17, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, cursor: 'pointer', color: 'var(--fg)' }}
          >Cancel</button>
          <button onClick={submit} disabled={busy}
            style={{
              flex: 2, padding: '18px 0', fontSize: 18, fontWeight: 900,
              background: busy ? 'var(--card-border)' : '#16a34a',
              color: busy ? 'var(--muted)' : '#fff',
              border: 'none', borderRadius: 10, cursor: busy ? 'default' : 'pointer',
            }}
          >
            {busy ? (uploading ? 'Uploading photos…' : 'Submitting…') : 'Submit Expense'}
          </button>
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 10, textAlign: 'center' }}>
          Remember to turn in the hardcopy receipt as usual.
        </div>
      </div>
    </div>
  );
}

function MyExpensesModal({ expenses, loading, onRefresh, onClose }) {
  const total = expenses.reduce((a, e) => a + Number(e.amount || 0), 0);
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14, width: '100%', maxWidth: 540, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 16, borderBottom: '1px solid var(--card-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 20 }}>My Expenses</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {expenses.length} receipt{expenses.length === 1 ? '' : 's'} · ${total.toFixed(2)} total
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onRefresh} className="btn-outline" style={{ padding: '6px 10px', fontSize: 12 }} disabled={loading}>
              {loading ? '…' : 'Refresh'}
            </button>
            <button onClick={onClose} className="btn-outline" style={{ padding: '6px 10px' }}>Close</button>
          </div>
        </div>

        <div style={{ overflowY: 'auto', padding: 12, display: 'grid', gap: 10 }}>
          {expenses.length === 0 && !loading && (
            <div className="muted" style={{ textAlign: 'center', padding: 24 }}>No expenses submitted yet.</div>
          )}
          {expenses.map(e => {
            let photoCount = 0;
            try { photoCount = JSON.parse(e.photo_paths || '[]').length; } catch (_) {}
            return (
              <div key={e.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 800, fontSize: 17 }}>
                    ${Number(e.amount).toFixed(2)} <span style={{ fontSize: 12, opacity: 0.6 }}>{e.currency}</span>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 99, background: '#ede9fe', color: '#6b21a8' }}>
                    {e.category}
                  </span>
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  {e.vendor || '—'}{e.expense_date ? ` · ${e.expense_date}` : ''}
                </div>
                {(e.rowo || e.trip_id) && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {e.rowo ? `Unit ${e.rowo}` : ''}{e.trip_id ? ` · Move #${e.trip_id}` : ''}
                  </div>
                )}
                <div style={{ fontSize: 11, marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {photoCount > 0 && <span style={{ padding: '2px 8px', borderRadius: 99, background: '#dbeafe', color: '#1e40af', fontWeight: 700 }}>📷 {photoCount}</span>}
                  {e.hardcopy_received
                    ? <span style={{ padding: '2px 8px', borderRadius: 99, background: '#dcfce7', color: '#166534', fontWeight: 700 }}>✓ Hardcopy received</span>
                    : <span style={{ padding: '2px 8px', borderRadius: 99, background: '#fef3c7', color: '#92400e', fontWeight: 700 }}>Hardcopy pending</span>}
                  {e.approved_at && <span style={{ padding: '2px 8px', borderRadius: 99, background: '#dcfce7', color: '#166534', fontWeight: 700 }}>✓ Approved</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── My Stats modal (driver portal) ─────────────────────────────────────────
function MyStatsModal({ stats, loading, driverName, onRefresh, onClose }) {
  const fmtH = (h) => {
    if (!Number.isFinite(h) || h <= 0) return "0 h";
    if (h < 1) return `${Math.round(h * 60)} min`;
    const whole = Math.floor(h);
    const mins  = Math.round((h - whole) * 60);
    return mins ? `${whole}h ${mins}m` : `${whole}h`;
  };
  const s = stats?.stats;
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 16, borderBottom: '1px solid var(--card-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 20 }}>My Stats</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{driverName} · all-time</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onRefresh} className="btn-outline" style={{ padding: '6px 10px', fontSize: 12 }} disabled={loading}>
              {loading ? '…' : 'Refresh'}
            </button>
            <button onClick={onClose} className="btn-outline" style={{ padding: '6px 10px' }}>Close</button>
          </div>
        </div>

        <div style={{ overflowY: 'auto', padding: 14 }}>
          {loading && !s && <div className="muted">Loading…</div>}
          {s && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
                <BigStat label="Moves Completed" value={s.moves_completed} sub={`${s.moves_in_progress} in progress`} color="#2563eb" />
                <BigStat label="Distance"          value={`${s.distance_km.toLocaleString()} km`} color="#059669" />
                <BigStat label="Hours"             value={fmtH(s.hours)} color="#7c3aed" />
                <BigStat label="Units Moved"       value={s.units_moved} color="#ea580c" />
                <BigStat label="Expenses"          value={`$${s.expense_total.toFixed(2)}`} sub={`${s.expense_count} receipts`} color="#9333ea" />
              </div>

              {stats?.recent_moves?.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.5, marginBottom: 6 }}>
                    Recent Moves
                  </div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {stats.recent_moves.map(m => (
                      <div key={m.trip_id} style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, padding: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>
                            #{m.trip_id}{m.unit_rowo ? ` · ${m.unit_rowo}` : ''}
                          </div>
                          {m.completed_at ? (
                            <span style={{ padding: '2px 8px', borderRadius: 99, background: '#dcfce7', color: '#166534', fontSize: 10, fontWeight: 700 }}>✓ DONE</span>
                          ) : (
                            <span style={{ padding: '2px 8px', borderRadius: 99, background: '#fef3c7', color: '#92400e', fontSize: 10, fontWeight: 700 }}>ACTIVE</span>
                          )}
                        </div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                          {m.destination ? `→ ${m.destination}` : ''}
                          {m.customer ? ` · ${m.customer}` : ''}
                          {m.distance_km ? ` · ${m.distance_km} km` : ''}
                        </div>
                        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                          {m.start_date || ''}{m.completed_at ? ` → ${String(m.completed_at).slice(0, 10)}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BigStat({ label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 12 }}>
      <div className="muted" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color, marginTop: 4 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Time Off list + request (driver portal) ────────────────────────────────
function TimeOffListModal({ entries, loading, onRequest, onRefresh, onDelete, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 16, borderBottom: '1px solid var(--card-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 20 }}>📅 My Time Off</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{entries.length} entr{entries.length === 1 ? 'y' : 'ies'}</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onRefresh} className="btn-outline" style={{ padding: '6px 10px', fontSize: 12 }} disabled={loading}>
              {loading ? '…' : 'Refresh'}
            </button>
            <button onClick={onClose} className="btn-outline" style={{ padding: '6px 10px' }}>Close</button>
          </div>
        </div>

        <div style={{ padding: 14 }}>
          <button
            onClick={onRequest}
            style={{
              width: '100%', padding: '18px 0', fontSize: 18, fontWeight: 900,
              background: '#0891b2', color: '#ffffff',
              border: 'none', borderRadius: 10, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(8,145,178,0.22)',
            }}
          >
            + Request Time Off
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0 14px 14px', display: 'grid', gap: 10 }}>
          {entries.length === 0 && !loading && (
            <div className="muted" style={{ textAlign: 'center', padding: 24 }}>No time off on file.</div>
          )}
          {entries.map(e => {
            const upcoming = String(e.start_date) > today;
            const active = String(e.start_date) <= today && String(e.end_date) >= today;
            const past = String(e.end_date) < today;
            const statusBg = active ? '#fee2e2' : upcoming ? '#dbeafe' : '#f3f4f6';
            const statusCol = active ? '#991b1b' : upcoming ? '#1e40af' : '#6b7280';
            return (
              <div key={e.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{e.start_date} → {e.end_date}</div>
                  <span style={{ padding: '2px 8px', borderRadius: 99, background: statusBg, color: statusCol, fontSize: 10, fontWeight: 700 }}>
                    {active ? 'ACTIVE' : upcoming ? 'UPCOMING' : 'PAST'}
                  </span>
                </div>
                {e.reason && <div className="muted" style={{ fontSize: 13, marginTop: 4, fontStyle: 'italic' }}>"{e.reason}"</div>}
                {upcoming && (
                  <button
                    onClick={() => onDelete(e.id)}
                    style={{
                      marginTop: 8, padding: '8px 12px', fontSize: 12, fontWeight: 700,
                      background: 'transparent', color: '#dc2626', border: '1px solid #fca5a5',
                      borderRadius: 8, cursor: 'pointer',
                    }}
                  >
                    Cancel this request
                  </button>
                )}
                {!past && !upcoming && (
                  <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>Already started — contact operations to cancel.</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TimeOffRequestModal({ dbId, sessionToken, onClose, onSubmitted, onAuthError }) {
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate]     = useState(today);
  const [reason, setReason]       = useState('');
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState('');

  const submit = async () => {
    setErr('');
    if (!startDate || !endDate) return setErr('Pick both dates.');
    if (endDate < startDate)    return setErr('End date must be on or after start date.');
    setBusy(true);
    try {
      const res = await fetch(`${API}/time-off`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          db: dbId, session_token: sessionToken,
          start_date: startDate, end_date: endDate, reason,
        }),
      });
      if (res.status === 401) return onAuthError?.();
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      onSubmitted?.();
    } catch (ex) {
      setErr(ex.message || 'Submit failed');
    } finally { setBusy(false); }
  };

  const bigInput = { width: '100%', padding: 16, fontSize: 20, boxSizing: 'border-box' };
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14, width: '100%', maxWidth: 460, maxHeight: '92vh', overflowY: 'auto', padding: 20 }}>
        <div style={{ fontWeight: 900, fontSize: 22, marginBottom: 4, color: '#0891b2' }}>📅 Request Time Off</div>
        <div className="muted" style={{ fontSize: 14, marginBottom: 16 }}>
          Dispatch will be notified. They may contact you if there's a scheduling conflict.
        </div>

        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Start date *</div>
            <input type="date" className="input" value={startDate} onChange={e => setStartDate(e.target.value)} style={bigInput} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>End date *</div>
            <input type="date" className="input" value={endDate} onChange={e => setEndDate(e.target.value)} style={bigInput} />
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Use the same date for a single day.</div>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Reason (optional)</div>
            <textarea
              className="input"
              value={reason}
              onChange={e => setReason(e.target.value)}
              style={{ ...bigInput, minHeight: 80, fontSize: 16 }}
              placeholder="e.g. Personal · medical · family"
            />
          </div>

          {err && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, color: '#dc2626', fontWeight: 700, fontSize: 14 }}>
              {err}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button onClick={onClose} disabled={busy}
            style={{ flex: 1, padding: '18px 0', fontSize: 17, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, cursor: 'pointer', color: 'var(--fg)' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
            style={{
              flex: 2, padding: '18px 0', fontSize: 18, fontWeight: 900,
              background: busy ? 'var(--card-border)' : '#0891b2',
              color: busy ? 'var(--muted)' : '#fff',
              border: 'none', borderRadius: 10, cursor: busy ? 'default' : 'pointer',
            }}>
            {busy ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Force-change-PIN modal (shown after portal login with must_change_pin) ──
// Gates the entire portal until the driver picks a new PIN. The temp PIN is
// pre-filled so we don't make them type on the touch keypad twice, but we
// still re-verify it server-side via POST /portal/change-pin for defense.
function ForceChangePinModal({ dbId, sessionToken, driverName, tempPin, onDone, onCancel }) {
  const [newPin, setNewPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    if (!/^\d{4,12}$/.test(newPin)) { setErr('PIN must be 4–12 digits.'); return; }
    if (newPin !== confirm)         { setErr("PINs don't match."); return; }
    if (newPin === tempPin)         { setErr('New PIN must differ from the temporary one.'); return; }

    setBusy(true);
    try {
      const r = await fetch('/api/portal/change-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          db:           dbId,
          session_token: sessionToken,
          current_pin:  tempPin,
          new_pin:      newPin,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      onDone?.(data);
    } catch (ex) {
      setErr(ex.message || 'PIN change failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '40px 16px', background: 'var(--bg)', color: 'var(--fg)' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{
          background: '#1d4ed8', color: '#fff', padding: '20px 22px', borderRadius: 12,
          marginBottom: 16, boxShadow: '0 4px 12px rgba(29,78,216,0.25)',
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.9 }}>
            🔑 Temporary PIN in use
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, marginTop: 4, lineHeight: 1.2 }}>
            Set a new PIN{driverName ? `, ${driverName}` : ''}
          </div>
          <div style={{ fontSize: 13, marginTop: 6, opacity: 0.95 }}>
            Pick something only you'll remember — 3 to 12 digits. You'll use this PIN on every sign-in.
          </div>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--muted)' }}>
            New PIN
          </div>
          <input
            className="input"
            type="password"
            inputMode="numeric"
            autoFocus
            maxLength={12}
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
            placeholder="4–12 digits"
            style={{ width: '100%', padding: 14, fontSize: 20, letterSpacing: 6, textAlign: 'center', boxSizing: 'border-box' }}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />

          <div style={{ fontSize: 12, fontWeight: 700, marginTop: 12, marginBottom: 4, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--muted)' }}>
            Confirm PIN
          </div>
          <input
            className="input"
            type="password"
            inputMode="numeric"
            maxLength={12}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value.replace(/\D/g, '').slice(0, 12))}
            placeholder="Type it again"
            style={{ width: '100%', padding: 14, fontSize: 20, letterSpacing: 6, textAlign: 'center', boxSizing: 'border-box' }}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />

          {err && (
            <div style={{ marginTop: 10, color: '#dc2626', fontSize: 13, fontWeight: 700 }}>
              {err}
            </div>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={busy || !newPin || !confirm}
            style={{
              marginTop: 14, width: '100%', padding: '14px 0', fontSize: 16, fontWeight: 900,
              background: (busy || !newPin || !confirm) ? 'var(--card-border)' : '#1d4ed8',
              color: '#fff', border: 'none', borderRadius: 10,
              cursor: (busy || !newPin || !confirm) ? 'default' : 'pointer',
            }}
          >
            {busy ? 'Saving…' : 'Set PIN & Continue'}
          </button>

          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              marginTop: 8, width: '100%', padding: '10px 0', fontSize: 13,
              background: 'transparent', border: '1px solid var(--card-border)',
              borderRadius: 8, cursor: 'pointer', color: 'var(--muted)',
            }}
          >
            Cancel — sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Hamburger drawer menu ─────────────────────────────────────────────────
// Right-side slide-in with the sections that used to live in the header row.
// Big tap targets, neon-red accent to match the hamburger, and Escape /
// click-outside dismiss.
function DriverPortalMenu({ driverName, terminal, onClose, onPick }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const items = [
    { key: 'home',      label: '🏠 Home',              sub: 'Assignments + scan a unit' },
    { key: 'stats',     label: '📊 My Stats',           sub: 'Moves, distance, expenses' },
    { key: 'expenses',  label: '🧾 Expenses',           sub: 'Submitted receipts + status' },
    { key: 'time_off',  label: '📅 Time Off',           sub: 'Request / view time off' },
    { key: 'documents', label: '📚 Company Documents',  sub: 'Insurance, permits, SOPs' },
    { key: 'logout',    label: '🚪 Log out',             sub: 'End your portal session', danger: true },
  ];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 950,
        background: 'rgba(15,23,42,0.65)',
        display: 'flex', justifyContent: 'flex-end',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        style={{
          width: 'min(320px, 85vw)', height: '100%',
          background: 'var(--card-bg)', color: 'var(--fg)',
          borderLeft: '2px solid #ff1744',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.35), -2px 0 0 rgba(255,23,68,0.35)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          background: '#0f172a', color: '#fff',
          padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '2px solid #ff1744',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: '#ff1744' }}>
              SNA Portal
            </div>
            <div style={{ fontSize: 16, fontWeight: 900, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {driverName || 'Driver'}
            </div>
            {terminal && (
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>🏠 {terminal}</div>
            )}
          </div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            style={{
              background: '#ff1744', color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 12px', fontWeight: 900, cursor: 'pointer',
              boxShadow: '0 0 10px rgba(255,23,68,0.5)',
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'grid', gap: 8 }}>
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              onClick={() => onPick?.(it.key)}
              style={{
                textAlign: 'left',
                background: it.danger ? 'rgba(220,38,38,0.08)' : 'var(--card-bg)',
                border: `1px solid ${it.danger ? '#fca5a5' : 'var(--card-border)'}`,
                borderRadius: 10,
                padding: '14px 14px',
                cursor: 'pointer',
                color: it.danger ? '#b91c1c' : 'var(--fg)',
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 800 }}>{it.label}</div>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{it.sub}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Company Documents panel (its own screen, reached from the drawer) ─────
// Full-screen sheet with a back button → landing. Replaces the old inline
// card that cluttered the landing page.
function DriverDocumentsPanel({ documents, loading, onRefresh, onOpenDoc, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 920,
      background: 'var(--bg)', color: 'var(--fg)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
        borderBottom: '1px solid var(--card-border)', background: 'var(--card-bg)',
      }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
            padding: '8px 14px', fontWeight: 800, cursor: 'pointer',
          }}
        >
          ← Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.7 }}>
            Home · Company Documents
          </div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>📚 Company Documents</div>
        </div>
        <button
          className="btn-outline"
          onClick={onRefresh}
          disabled={loading}
          style={{ fontSize: 12, padding: '6px 10px' }}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16, maxWidth: 600, width: '100%', margin: '0 auto' }}>
        <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
          Reference documents and forms. Tap to view, print, or share.
        </div>

        {loading && documents.length === 0 ? (
          <div className="muted">Loading…</div>
        ) : documents.length === 0 ? (
          <div className="card" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>No documents yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              Dispatch will upload company documents (insurance, permits, SOPs) here.
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {documents.map((d) => {
              const kindLabel = d.kind === 'insurance'        ? '🛡️ Insurance'
                              : d.kind === 'permit_template'  ? '📄 Permit'
                              : d.kind === 'sop'              ? '📖 SOP'
                              : d.kind === 'safety'           ? '⚠️ Safety'
                              : d.kind === 'form'             ? '📝 Form'
                              : '📎 Doc';
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onOpenDoc?.(d)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                    padding: '12px 14px', borderRadius: 10,
                    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                    color: 'var(--fg)', fontSize: 14, textAlign: 'left', cursor: 'pointer',
                  }}
                >
                  <span style={{ fontWeight: 800 }}>{kindLabel}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.label || d.original_name}
                  </span>
                  <span style={{ fontSize: 11, opacity: 0.7, color: '#1d4ed8' }}>View ↗</span>
                  {d.notes && (
                    <div style={{ flexBasis: '100%', fontSize: 12, color: '#475569', marginTop: 4 }}>
                      {d.notes}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
